<template>
  <div>
    <head>
      <meta charset="utf-8" />
      <title>CBHI | Community-Based Health Insurance</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="Keep your finances and membership up to date" name="description" />
      <meta content="Kelvin Njuguna Ndungu" name="author" />
      <!-- App favicon -->
      <link rel="shortcut icon" href="assets/images/favicon.svg">

      <!-- Bootstrap Css -->
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <!-- Icons Css -->
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />

      <!-- Bootstrap select styling -->
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">

      <!-- App Css-->
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <!-- custom Css-->
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body data-sidebar="dark">
    <!-- Begin page -->
    <div id="layout-wrapper">
      <!-- ========== Left Sidebar Start ========== -->
      <NavigationBar/>
      <!-- Left Sidebar End -->

      <!-- ============================================================== -->
      <!-- Start right Content here -->
      <!-- ============================================================== -->
      <div class="main-content">
        <div class="page-content">
          <div class="container-fluid">
            <!-- start page title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Receive Premium Payment</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript: void(0);">Premium Collection</a></li>
                      <li class="breadcrumb-item active">Receive Payment</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div class="row justify-content-center align-items-center d-flex">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                    <div class="btn-toolbar p-3 d-flex flex-column w-100" role="toolbar">
                      <h4 class="card-title text-uppercase mb-1">
                        Premium Payment Collection
                      </h4>
                      <p class="pb-0 mb-0">Collect annual premium payments from existing CBHI members to renew their membership.</p>
                    </div>
                  </div>

                  <div class="card-body">
                    <!-- Alerts -->
                    <div v-if="alert.success" class="alert alert-success" role="alert">
                      {{message}}
                    </div>
                    <div v-if="alert.info" class="alert alert-primary" role="alert">
                      {{message}}
                    </div>
                    <div v-if="alert.failed" class="alert alert-danger" role="alert">
                      {{message}}
                    </div>

                    <!-- Member Search Section -->
                    <div class="row">
                      <div class="col-12">
                        <div class="bg-info border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                          <p class="fw-semibold mb-0 pb-0 text-uppercase">Member Lookup</p>
                        </div>
                      </div>

                      <div class="col-md-6 mb-3">
                        <label for="search-input" class="form-label">Search Member <strong class="text-danger fw-bold">*</strong></label>
                        <div class="input-group">
                          <input
                              v-model="searchQuery"
                              type="text"
                              class="form-control"
                              id="search-input"
                              placeholder="Enter CBHI Number, Phone Number, or Name"
                              @keyup.enter="searchMember"
                          >
                          <button @click="searchMember" class="btn btn-primary" type="button" :disabled="searchLoading">
                            <span v-if="searchLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            <i v-else class="bx bx-search-alt font-size-16 align-middle me-2"></i>
                            Search
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Member Details Section -->
                    <div v-if="memberData" class="row mt-4">
                      <!-- Inactive Member Warning -->
                      <div v-if="memberData.status === 'inactive'" class="col-12 mb-3">
                        <div class="alert alert-danger">
                          <i class="bx bx-error-circle me-2"></i>
                          <strong>Member Status: INACTIVE</strong>
                          <p class="mb-0 mt-2">This member is currently inactive. Membership becomes active after premium payment is received and processed.</p>
                        </div>
                      </div>

                      <!-- Suspended Member Warning -->
                      <div v-if="memberData.status === 'suspended'" class="col-12 mb-3">
                        <div class="alert alert-warning">
                          <i class="bx bx-error-circle me-2"></i>
                          <strong>Member Status: SUSPENDED</strong>
                          <p class="mb-0 mt-2" v-if="memberData.suspension_reason">Reason: {{ memberData.suspension_reason }}</p>
                        </div>
                      </div>

                      <div class="col-12">
                        <div class="bg-info bg-soft border-2 p-3 mb-4 d-flex align-items-center">
                          <p class="fw-semibold mb-0 text-uppercase text-dark">Member Information</p>
                        </div>
                      </div>

                      <div class="col-md-12">
                        <div class="table-responsive">
                          <table class="table table-bordered">
                            <tbody>
                            <tr>
                              <td class="bg-light fw-semibold" width="25%">CBHI Number</td>
                              <td>{{ memberData.cbhi_number }}</td>
                              <td class="bg-light fw-semibold" width="25%">Member ID</td>
                              <td>{{ memberData.member_id }}</td>
                            </tr>
                            <tr>
                              <td class="bg-light fw-semibold">Full Name</td>
                              <td>{{ memberFullName }}</td>
                              <td class="bg-light fw-semibold">Gender</td>
                              <td>{{ memberData.gender }}</td>
                            </tr>
                            <tr>
                              <td class="bg-light fw-semibold">Phone Number</td>
                              <td>{{ memberData.phone_number || '-' }}</td>
                              <td class="bg-light fw-semibold">National ID</td>
                              <td>{{ memberData.national_id || '-' }}</td>
                            </tr>
                            <tr>
                              <td class="bg-light fw-semibold">Membership Status</td>
                              <td>
                                <span v-if="memberData.status === 'active'" class="badge bg-success text-uppercase">{{ memberData.status }}</span>
                                <span v-else-if="memberData.status === 'inactive'" class="badge bg-danger text-uppercase">{{ memberData.status }}</span>
                                <span v-else-if="memberData.status === 'suspended'" class="badge bg-warning text-uppercase">{{ memberData.status }}</span>
                                <span v-else class="badge bg-secondary text-uppercase">{{ memberData.status }}</span>
                              </td>
                              <td class="bg-light fw-semibold">Payment Status</td>
                              <td>
                                <span v-if="outstandingBalance <= 0" class="badge bg-success">FULLY PAID</span>
                                <span v-else-if="currentYearPayments > 0" class="badge bg-warning">PARTIALLY PAID</span>
                                <span v-else class="badge bg-danger">NOT PAID</span>
                              </td>
                            </tr>
                            <tr>
                              <td class="bg-light fw-semibold">Region / Zone / Woreda</td>
                              <td>{{ memberData.region_name }} / {{ memberData.zone_name }} / {{ memberData.woreda_name }}</td>
                              <td class="bg-light fw-semibold">Kebele</td>
                              <td>{{ memberData.kebele_name }}</td>
                            </tr>
                            <tr>
                              <td class="bg-light fw-semibold">Registration Date</td>
                              <td>{{ formatDate(memberData.registration_date) }}</td>
                              <td class="bg-light fw-semibold">Expiry Date</td>
                              <td>
                                {{ formatDate(memberData.expiry_date) }}
                                <span v-if="memberData.status === 'inactive'" class="badge bg-danger ms-2">INACTIVE - PAYMENT REQUIRED</span>
                                <span v-else-if="memberData.status === 'suspended'" class="badge bg-warning ms-2">SUSPENDED</span>
                                <span v-else-if="isExpired(memberData.expiry_date)" class="badge bg-danger ms-2">EXPIRED</span>
                                <span v-else-if="isExpiringSoon(memberData.expiry_date)" class="badge bg-warning ms-2">EXPIRING SOON</span>
                                <span v-else class="badge bg-success ms-2">ACTIVE</span>
                              </td>
                            </tr>
                            <tr>
                              <td class="bg-light fw-semibold">Selected Plan</td>
                              <td>
                                <span v-if="selectedPlan">
                                  <strong>{{ selectedPlan.plan_name }}</strong>
                                  <span class="badge bg-info ms-2">{{ selectedPlan.plan_type.toUpperCase() }}</span>
                                </span>
                                <span v-else class="text-danger">No plan assigned</span>
                              </td>
                              <td class="bg-light fw-semibold">Total Beneficiaries</td>
                              <td>{{ totalBeneficiaries }}</td>
                            </tr>
                            <tr>
                              <td class="bg-light fw-semibold">Last Payment Date</td>
                              <td colspan="3">{{ memberData.last_payment_date ? formatDate(memberData.last_payment_date) : 'No previous payments' }}</td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Plan Details Card -->
                      <div class="col-12 mb-3" v-if="selectedPlan">
                        <div class="card bg-light border-success">
                          <div class="card-header bg-success text-white">
                            <h6 class="card-title mb-0" style="color: white">
                              <i class="bx bx-shield-check me-2"></i>
                              {{ selectedPlan.plan_name }} - Premium Breakdown for {{ currentYear }}
                            </h6>
                          </div>
                          <div class="card-body">
                            <div class="row">
                              <div class="col-md-8">
                                <table class="table table-sm mb-0">
                                  <tbody>
                                  <tr>
                                    <td>Annual Premium (as registered):</td>
                                    <td class="text-end"><strong>ETB {{ formatCurrency(annualPremium) }}</strong></td>
                                  </tr>
                                  <tr>
                                    <td>Primary Member + {{ totalDependents }} Dependent(s):</td>
                                    <td class="text-end"><strong>{{ totalBeneficiaries }} person(s) covered</strong></td>
                                  </tr>
                                  <tr v-if="currentYearPayments > 0" class="text-success">
                                    <td>Already Paid ({{ currentYear }}):</td>
                                    <td class="text-end"><strong>- ETB {{ formatCurrency(currentYearPayments) }}</strong></td>
                                  </tr>
                                  <tr class="border-top">
                                    <td><strong>Outstanding Balance:</strong></td>
                                    <td class="text-end"><strong class="text-primary">ETB {{ formatCurrency(outstandingBalance) }}</strong></td>
                                  </tr>
                                  </tbody>
                                </table>
                              </div>
                              <div class="col-md-4">
                                <div class="bg-white p-3 rounded">
                                  <h6 class="mb-2">Plan Benefits:</h6>
                                  <div class="row" v-if="selectedPlan.benefits">
                                    <div class="col-12">
                                      <ul class="list-unstyled mb-0 small">
                                        <li v-if="selectedPlan.benefits.outpatient" class="mb-1">
                                          <i class="bx bx-check text-success me-1"></i>Outpatient Care
                                        </li>
                                        <li v-if="selectedPlan.benefits.inpatient" class="mb-1">
                                          <i class="bx bx-check text-success me-1"></i>Inpatient Care
                                        </li>
                                        <li v-if="selectedPlan.benefits.emergency" class="mb-1">
                                          <i class="bx bx-check text-success me-1"></i>Emergency Care
                                        </li>
                                        <li v-if="selectedPlan.benefits.maternity" class="mb-1">
                                          <i class="bx bx-check text-success me-1"></i>Maternity Care
                                        </li>
                                        <li v-if="selectedPlan.benefits.dental" class="mb-1">
                                          <i class="bx bx-check text-success me-1"></i>Dental Care
                                        </li>
                                        <li v-if="selectedPlan.benefits.vision" class="mb-1">
                                          <i class="bx bx-check text-success me-1"></i>Vision Care
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                  <small class="text-muted">
                                    Annual Limit: ETB {{ formatCurrency(selectedPlan.annual_coverage_limit) }}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- No Plan Alert -->
                      <div v-if="!selectedPlan && memberData" class="col-12 mb-3">
                        <div class="alert alert-warning">
                          <i class="bx bx-error-circle me-2"></i>
                          <strong>No Plan Assigned:</strong> This member does not have a health insurance plan assigned.
                          Please assign a plan before collecting premium payments.
                          <div class="mt-2">
                            <button @click="viewMemberDetails" class="btn btn-warning btn-sm">
                              <i class="bx bx-edit me-1"></i> Assign Plan
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Payment History -->
                      <div v-if="paymentHistory.length > 0" class="col-12 mt-3">
                        <h5 class="mb-3">Recent Payment History</h5>
                        <div class="table-responsive">
                          <table class="table table-striped">
                            <thead>
                            <tr>
                              <th>Payment Date</th>
                              <th>Receipt Number</th>
                              <th>Amount</th>
                              <th>Payment Method</th>
                              <th>Status</th>
                              <th>Collected By</th>
                              <th class="text-center">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="payment in paymentHistory" :key="payment.payment_id">
                              <td>
                                <strong>{{ formatDate(payment.payment_date) }}</strong>
                                <p class="mb-0 text-muted fs-12">Year {{ payment.payment_year }}</p>
                              </td>
                              <td>{{ payment.receipt_number || payment.payment_reference }}</td>
                              <td>
                                <strong>ETB {{ formatCurrency(payment.amount) }}</strong>
                              </td>
                              <td>
                                <div class="d-flex align-items-center">
                                  <i :class="getPaymentMethodIcon(payment.method_name)" class="me-2 fs-5"></i>
                                  <div>
                                    <strong>{{ payment.method_name }}</strong>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span :class="getStatusClass(payment.status)" class="text-uppercase">
                                  <i class="bx bx-check me-1" v-if="payment.status === 'completed'"></i>
                                  {{ payment.status }}
                                </span>
                              </td>
                              <td>{{ payment.collected_by || 'System' }}</td>
                              <td class="text-center">
                                <div class="btn-group" role="group">
                                  <button class="btn btn-outline-primary btn-sm me-1"
                                          title="View Receipt"
                                          @click="viewReceipt(payment)">
                                    <i class="bx bx-file-blank"></i>
                                  </button>
                                  <button class="btn btn-outline-secondary btn-sm"
                                          title="Download Receipt"
                                          @click="downloadReceipt(payment)">
                                    <i class="bx bx-download"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Payment Summary Footer -->
                  <div v-if="memberData && selectedPlan" class="card-footer bg-light border-top py-3 border border-dashed border-info d-flex flex-wrap">
                    <div class="row w-100">
                      <div class="col-md-4 mb-3">
                        <span class="fw-semibold text-dark d-block">Annual Premium ({{ selectedPlan.plan_name }})</span>
                        <h4 class="amount fs-18 fw-bold my-1 d-block">ETB <span>{{ formatCurrency(annualPremium) }}</span></h4>
                        <span class="deals fs-12 text-muted d-block">{{ totalBeneficiaries }} beneficiaries</span>
                      </div>

                      <div class="col-md-4 mb-3">
                        <span class="fw-semibold text-dark d-block">Already Paid ({{ currentYear }})</span>
                        <h4 class="amount fs-18 fw-bold my-1 d-block text-success">ETB <span>{{ formatCurrency(currentYearPayments) }}</span></h4>
                        <span class="deals fs-12 text-muted d-block">Payments received</span>
                      </div>

                      <div class="col-md-4 mb-3">
                        <span class="fw-semibold text-dark d-block">Outstanding Balance</span>
                        <h4 class="amount fs-18 fw-bold my-1 d-block text-primary">ETB <span>{{ formatCurrency(outstandingBalance) }}</span></h4>
                        <span class="deals fs-12 text-muted d-block" v-if="outstandingBalance > 0">Amount still due</span>
                        <span class="deals fs-12 text-success d-block" v-else>Fully paid</span>
                      </div>
                    </div>
                  </div>

                  <div v-if="memberData" class="card-footer bg-white border-top py-3">
                    <div class="col-12">
                      <button @click="openPaymentModal" type="button" class="btn btn-primary waves-effect waves-light text-uppercase" :disabled="!selectedPlan || outstandingBalance <= 0">
                        <i class="mdi mdi-cash-multiple font-size-16 align-middle me-2"></i>
                        <span v-if="!selectedPlan">No Plan Assigned</span>
                        <span v-else-if="outstandingBalance > 0">Record Payment (ETB {{ formatCurrency(outstandingBalance) }})</span>
                        <span v-else>Fully Paid</span>
                      </button>

                      <button @click="clearForm" type="button" class="btn btn-outline-secondary waves-effect text-uppercase mx-3">
                        <i class="mdi mdi-close font-size-16 align-middle me-2"></i> Clear
                      </button>

                      <button @click="viewMemberDetails" type="button" class="btn btn-outline-info waves-effect text-uppercase">
                        <i class="bx bx-user font-size-16 align-middle me-2"></i> View Full Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- container-fluid -->
        </div>
        <!-- End Page-content -->

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">
                  Ministry of Health - Ethiopia
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <!-- end main content-->
    </div>
    <!-- END layout-wrapper -->

    <!-- Payment Modal Component -->
    <PaymentModal
        ref="paymentModal"
        :member-info="memberData || {}"
        :annual-premium="outstandingBalance"
        :total-beneficiaries="totalBeneficiaries"
        @payment-recorded="handlePaymentRecorded"
        @payment-error="handlePaymentError"
        @download-receipt="handleDownloadReceipt"
    />

    <!-- Receipt Modal Component -->
    <ReceiptModal
        ref="receiptModal"
        :member-info="memberData || {}"
        :total-beneficiaries="totalBeneficiaries"
        :expected-annual-premium="annualPremium"
        :total-paid-for-year="currentYearPayments"
    />

    <!-- Right bar overlay-->
    <div class="rightbar-overlay"></div>
    </body>
  </div>
</template>

<script>
import NavigationBar from "@/components/Navigation.vue";
import PaymentModal from "@/components/cbhi-member-management/modals/PaymentModal.vue";
import ReceiptModal from "@/components/cbhi-member-management/modals/ReceiptModal.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "ReceivePremium",
  components: {
    NavigationBar,
    PaymentModal,
    ReceiptModal
  },
  data() {
    return {
      // Loading states
      searchLoading: false,

      // Search
      searchQuery: '',

      // Member data
      memberData: null,
      paymentHistory: [],
      currentYearPayments: 0,

      // Plan details (for display purposes only)
      selectedPlan: null,
      planLoading: false,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: '',

      // Current year
      currentYear: new Date().getFullYear()
    };
  },

  computed: {
    memberFullName() {
      if (!this.memberData) return '';
      return this.memberData.full_name || '';
    },

    // Use the annual_premium directly from API - no frontend calculation
    annualPremium() {
      if (!this.memberData) return 0;
      return parseFloat(this.memberData.annual_premium) || 0;
    },

    // Outstanding balance = Annual Premium from API - Payments already made
    outstandingBalance() {
      const remainingBalance = this.annualPremium - this.currentYearPayments;
      return Math.max(0, remainingBalance);
    },

    // Count ALL beneficiaries (not just active ones)
    totalBeneficiaries() {
      if (!this.memberData) return 0;
      // Primary member (1) + all registered dependents
      const dependentsCount = this.memberData.beneficiaries ? this.memberData.beneficiaries.length : 0;
      return 1 + dependentsCount;
    },

    totalDependents() {
      return this.totalBeneficiaries - 1;
    }
  },

  methods: {
    checkAuth() {
      const token = localStorage.getItem("authToken");
      if (!token) {
        router.push('/login');
      }
    },

    async loadPlanDetails() {
      if (!this.memberData.selected_plan_id) {
        console.log('No plan selected for this member');
        this.selectedPlan = null;
        return;
      }

      this.planLoading = true;

      const data = {
        "function": "getPlanDetails",
        "planId": this.memberData.selected_plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.selectedPlan = res.data.data.plan;
          console.log('Plan loaded:', this.selectedPlan);
        } else {
          console.error('Failed to load plan details:', res.data.message);
          this.selectedPlan = null;
          this.showAlert('failed', 'Failed to load member plan details');
        }
      } catch (e) {
        console.error("Failed to load plan details:", e);
        this.selectedPlan = null;
        this.showAlert('failed', 'Error loading member plan details');
      } finally {
        this.planLoading = false;
      }
    },

    isExpired(date) {
      if (!date) return true;
      return new Date(date) < new Date();
    },

    isExpiringSoon(date) {
      if (!date) return false;
      const expiryDate = new Date(date);
      const today = new Date();
      const daysUntilExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
    },

    // Check if member is truly active (both status and expiry must be valid)
    isMemberActive() {
      if (!this.memberData) return false;
      const statusActive = this.memberData.status === 'active';
      const notExpired = !this.isExpired(this.memberData.expiry_date);
      return statusActive && notExpired;
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    async searchMember() {
      if (!this.searchQuery.trim()) {
        this.showAlert('failed', 'Please enter a search term');
        return;
      }

      this.searchLoading = true;
      this.clearAlerts();
      this.memberData = null;
      this.selectedPlan = null;
      this.paymentHistory = [];
      this.currentYearPayments = 0;

      const data = {
        "function": "searchMembers",
        "searchTerm": this.searchQuery.trim(),
        "limit": 1
      };

      try {
        const res = await executeJson(data, authUrl);
        console.log('API Response:', res.data);

        // Handle double-nested response: res.data.data.data.members
        const responseData = res.data.data || res.data;
        const membersData = responseData.data || responseData;

        if (res.data.success && membersData.members && membersData.members.length > 0) {
          const members = membersData.members;

          // Try to find exact match first
          let selectedMember = members.find(m =>
              m.cbhi_number === this.searchQuery.trim() ||
              m.member_id == this.searchQuery.trim()
          );

          // If no exact match, take the first result
          if (!selectedMember) {
            selectedMember = members[0];
          }

          this.memberData = selectedMember;
          console.log('Member Data:', this.memberData);
          console.log('Annual Premium from API:', this.memberData.annual_premium);

          // Log beneficiaries info
          if (this.memberData.beneficiaries && Array.isArray(this.memberData.beneficiaries)) {
            console.log('Total beneficiaries (dependents):', this.memberData.beneficiaries.length);
            console.log('Beneficiaries:', this.memberData.beneficiaries);
          }

          // Load plan details (for display purposes) and payment history
          await this.loadPlanDetails();
          await this.loadPaymentHistory();
          this.calculateCurrentYearPayments();

          console.log('Final values - Annual Premium:', this.annualPremium, 'Outstanding:', this.outstandingBalance);
        } else {
          this.showAlert('failed', 'No member found with the provided search criteria');
        }
      } catch (e) {
        console.error('Search error:', e);
        this.showAlert('failed', e.message || 'Failed to search member');
      } finally {
        this.searchLoading = false;
      }
    },

    async loadPaymentHistory() {
      if (!this.memberData || !this.memberData.member_id) return;

      const data = {
        "function": "getPaymentHistory",
        "memberId": this.memberData.member_id,
        "limit": 5
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success && res.data.data.payments) {
          this.paymentHistory = res.data.data.payments || [];
        }
      } catch (e) {
        console.error("Failed to load payment history:", e);
        this.paymentHistory = [];
      }
    },

    calculateCurrentYearPayments() {
      this.currentYearPayments = this.paymentHistory
          .filter(payment =>
              payment.payment_year == this.currentYear &&
              payment.status === 'completed'
          )
          .reduce((total, payment) => total + parseFloat(payment.amount || 0), 0);

      console.log('Current year payments:', this.currentYearPayments);
    },

    openPaymentModal() {
      if (!this.memberData) {
        this.showAlert('failed', 'No member selected');
        return;
      }

      if (!this.selectedPlan) {
        this.showAlert('failed', 'Member does not have a plan assigned. Please assign a plan first.');
        return;
      }

      if (this.outstandingBalance <= 0) {
        this.showAlert('info', 'Member has already paid the full annual premium for ' + this.currentYear);
        return;
      }

      this.$refs.paymentModal.openModal();
    },

    handlePaymentRecorded(event) {
      this.showAlert('success', event.message);
      this.loadPaymentHistory();
      setTimeout(() => {
        this.calculateCurrentYearPayments();
      }, 500);
    },

    handlePaymentError(event) {
      this.showAlert('failed', event.message);
    },

    handleDownloadReceipt(event) {
      this.$refs.receiptModal.openModal(event.payment);
    },

    getPaymentMethodIcon(method) {
      const methodLower = method.toLowerCase();
      if (methodLower.includes('telebirr') || methodLower.includes('mpesa') || methodLower.includes('cbe')) {
        return 'bx bx-mobile-alt text-success';
      }
      if (methodLower.includes('bank')) {
        return 'bx bx-building-house text-primary';
      }
      if (methodLower.includes('cash')) {
        return 'bx bx-money text-warning';
      }
      return 'bx bx-credit-card text-info';
    },

    viewReceipt(payment) {
      this.$refs.receiptModal.openModal(payment);
    },

    downloadReceipt(payment) {
      this.$refs.receiptModal.openModal(payment);
      setTimeout(() => {
        this.$refs.receiptModal.downloadReceipt();
      }, 1000);
    },

    viewMemberDetails() {
      sessionStorage.setItem('member', JSON.stringify(this.memberData));
      router.push({
        name: 'member-details',
        params: {
          cbhi_number: this.memberData.cbhi_number
        }
      });
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    getStatusClass(status) {
      const statusClasses = {
        'completed': 'badge bg-success',
        'pending': 'badge bg-warning',
        'failed': 'badge bg-danger',
        'reversed': 'badge bg-secondary'
      };
      return statusClasses[status] || 'badge bg-secondary';
    },

    clearForm() {
      this.searchQuery = '';
      this.memberData = null;
      this.selectedPlan = null;
      this.paymentHistory = [];
      this.currentYearPayments = 0;
      this.clearAlerts();
    }
  }
};
</script>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.alert {
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.bg-soft {
  background-color: rgba(var(--bs-info-rgb), 0.1) !important;
}

.text-muted {
  color: #6c757d !important;
}

.fw-semibold {
  font-weight: 600 !important;
}

.fs-18 {
  font-size: 1.125rem !important;
}

.amount {
  color: #495057;
}

.btn-group {
  display: flex;
  gap: 0.25rem;
}

.fs-12 {
  font-size: 0.75rem;
}

.deals {
  font-size: 0.75rem;
}

.card.border-success {
  border-color: #28a745 !important;
  border-width: 2px;
}

.card-header.bg-success {
  background-color: #28a745 !important;
  color: white !important;
}

.bx-check {
  color: #28a745;
}

.list-unstyled {
  padding-left: 0;
  list-style: none;
}
</style>